/**
 * TaskDetail Component
 * 
 * Displays comprehensive task information in the Tasks module.
 * Shows task overview, description, subtasks, activity timeline, attachments, and comments.
 * 
 * @param {string} taskId - The ID of the task
 */

import { useState } from 'react';
import { Row, Col, Badge, ProgressBar, Button, Dropdown, Table, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './TaskDetail.css';
import { 
    Pagination,
    AddTaskModal,
    MoveOrCopyTaskModal,
    QuickAddSubtasksModal,
    DeleteTaskModal
} from '../../../components/MyTasks/components';
import {
    AddSubtaskModal,
    UploadAttachmentModal,
    AddCommentModal,
    ChangeStatusModal,
    UpdatePriorityModal,
    ViewAssigneesModal
} from './TaskDetailModals';
import { FilterTaskActivityOffcanvas, ViewHistoryOffcanvas } from './TaskDetailOffcanvas';

const TaskDetail = ({ taskId }) => {
    // Modal states
    const [showEditTask, setShowEditTask] = useState(false);
    const [showAddSubtask, setShowAddSubtask] = useState(false);
    const [showUploadAttachment, setShowUploadAttachment] = useState(false);
    const [showAddComment, setShowAddComment] = useState(false);
    const [showViewHistory, setShowViewHistory] = useState(false);
    const [showChangeStatus, setShowChangeStatus] = useState(false);
    const [showUpdatePriority, setShowUpdatePriority] = useState(false);
    const [showActivityFilter, setShowActivityFilter] = useState(false);
    const [showViewAssignees, setShowViewAssignees] = useState(false);
    
    // Subtask action modals
    const [showMoveOrCopy, setShowMoveOrCopy] = useState(false);
    const [showQuickAddSubtasks, setShowQuickAddSubtasks] = useState(false);
    const [showDeleteSubtask, setShowDeleteSubtask] = useState(false);
    const [showEditSubtask, setShowEditSubtask] = useState(false);
    const [selectedSubtask, setSelectedSubtask] = useState(null);
    
    // Move or Copy modal states
    const [moveOrCopyType, setMoveOrCopyType] = useState('move');
    const [targetProject, setTargetProject] = useState(null);
    const [targetTaskList, setTargetTaskList] = useState(null);
    
    // Quick Add Subtasks modal state
    const [subtasksList, setSubtasksList] = useState([]);

    // Quick edit states
    const [currentStatus, setCurrentStatus] = useState('In Progress');
    const [currentPriority, setCurrentPriority] = useState('High');

    // Filter states
    const [activityDateRange, setActivityDateRange] = useState(null);
    const [activityTypes, setActivityTypes] = useState([]);
    const [activityUsers, setActivityUsers] = useState([]);

    // Pagination states
    const [pageSize, setPageSize] = useState(5);
    const [currentPage, setCurrentPage] = useState(1);

    // Subtasks state
    const [subtasksData, setSubtasksData] = useState([
        { 
            id: 1, 
            name: 'Hero section design',
            team: 'Frontend Tasks',
            status: 'Completed', 
            assignee: 'Sarah Wilson', 
            startDate: 'Nov 25, 2024',
            dueDate: 'Nov 30, 2024', 
            priority: 'High',
            estimated: '8h',
            logged: '7h 30m',
            avatar: 'https://randomuser.me/api/portraits/women/50.jpg', 
            initials: 'SW',
            completed: true
        },
        { 
            id: 2, 
            name: 'Product grid layout',
            team: 'Frontend Tasks',
            status: 'In Progress', 
            assignee: 'Sarah Wilson', 
            startDate: 'Nov 28, 2024',
            dueDate: 'Dec 2, 2024', 
            priority: 'High',
            estimated: '12h',
            logged: '8h',
            avatar: 'https://randomuser.me/api/portraits/women/50.jpg', 
            initials: 'SW',
            completed: false
        },
        { 
            id: 3, 
            name: 'Mobile responsive design',
            team: 'Frontend Tasks',
            status: 'To Do', 
            assignee: 'Mike Johnson', 
            startDate: 'Dec 3, 2024',
            dueDate: 'Dec 5, 2024', 
            priority: 'Medium',
            estimated: '10h',
            logged: '0h',
            avatar: 'https://randomuser.me/api/portraits/men/45.jpg', 
            initials: 'MJ',
            completed: false
        },
        { 
            id: 4, 
            name: 'Review and finalize',
            team: 'Backend Tasks',
            status: 'To Do', 
            assignee: 'Sarah Wilson', 
            startDate: 'Dec 5, 2024',
            dueDate: 'Dec 5, 2024', 
            priority: 'Low',
            estimated: '4h',
            logged: '0h',
            avatar: 'https://randomuser.me/api/portraits/women/50.jpg', 
            initials: 'SW',
            completed: false
        },
        { 
            id: 5, 
            name: 'Mobile responsive design',
            team: 'Frontend Tasks',
            status: 'To Do', 
            assignee: 'Mike Johnson', 
            startDate: 'Dec 3, 2024',
            dueDate: 'Dec 5, 2024', 
            priority: 'Medium',
            estimated: '10h',
            logged: '0h',
            avatar: 'https://randomuser.me/api/portraits/men/45.jpg', 
            initials: 'MJ',
            completed: false
        },
        { 
            id: 6, 
            name: 'Review and finalize',
            team: 'Backend Tasks',
            status: 'To Do', 
            assignee: 'Sarah Wilson', 
            startDate: 'Dec 5, 2024',
            dueDate: 'Dec 5, 2024', 
            priority: 'Low',
            estimated: '4h',
            logged: '0h',
            avatar: 'https://randomuser.me/api/portraits/women/50.jpg', 
            initials: 'SW',
            completed: false
        }
    ]);

    // Toggle subtask completion
    const toggleSubtaskCompletion = (subtaskId) => {
        setSubtasksData(prevSubtasks =>
            prevSubtasks.map(subtask =>
                subtask.id === subtaskId
                    ? {
                        ...subtask,
                        completed: !subtask.completed,
                        status: !subtask.completed ? 'Completed' : 'To Do'
                    }
                    : subtask
            )
        );
    };

    // Pagination calculations
    const totalSubtasks = subtasksData.length;
    const totalPages = Math.ceil(totalSubtasks / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedSubtasks = subtasksData.slice(startIndex, endIndex);

    // Mock options for modals (matching listing page)
    const projectOptions = [
        { value: 1, label: 'Website Redesign' },
        { value: 2, label: 'Mobile App Development' },
        { value: 3, label: 'Marketing Campaign' }
    ];

    const taskListOptions = [
        { value: 1, label: 'Frontend Tasks' },
        { value: 2, label: 'Backend Tasks' },
        { value: 3, label: 'Design Tasks' }
    ];

    // Modal handlers
    const handleMoveOrCopyClose = () => {
        setShowMoveOrCopy(false);
        setSelectedSubtask(null);
        setMoveOrCopyType('move');
        setTargetProject(null);
        setTargetTaskList(null);
    };

    const handleMoveOrCopySubmit = () => {
        console.log('Move/Copy submitted');
        handleMoveOrCopyClose();
    };

    const handleQuickAddSubtasksClose = () => {
        setShowQuickAddSubtasks(false);
        setSelectedSubtask(null);
        setSubtasksList([]);
    };

    const handleQuickAddSubtasksSubmit = () => {
        console.log('Quick add subtasks submitted');
        handleQuickAddSubtasksClose();
    };

    const handleDeleteSubtaskClose = () => {
        setShowDeleteSubtask(false);
        setSelectedSubtask(null);
    };

    const handleDeleteSubtask = () => {
        console.log('Delete subtask:', selectedSubtask);
        handleDeleteSubtaskClose();
    };

    const handleEditSubtaskClose = () => {
        setShowEditSubtask(false);
        setSelectedSubtask(null);
    };

    // Status and Priority options
    const statusOptions = [
        'Backlog',
        'To Do',
        'In Progress',
        'TL Review',
        'Reopened',
        'PM Review',
        'Send to Development',
        'Assign to QA',
        'Bug Fixing',
        'QA Bug Verification',
        'Deploy to Beta or Live',
        'On Hold',
        'Complete'
    ];
    const priorityOptions = ['Low', 'Medium', 'High'];

    // Mock data - Replace with actual API call
    const taskData = {
        id: taskId || 'TASK-001',
        name: 'Design Homepage Mockup',
        status: currentStatus,
        priority: currentPriority,
        createdDate: 'Nov 28, 2024',
        dueDate: 'Dec 5, 2024',
        assignedTo: [
            { id: 1, name: 'Sarah Wilson', avatar: 'https://randomuser.me/api/portraits/women/50.jpg', initials: 'SW', department: 'Design', designation: 'Senior UI/UX Designer', email: 'sarah.wilson@company.com' },
            { id: 2, name: 'Mike Johnson', avatar: 'https://randomuser.me/api/portraits/men/45.jpg', initials: 'MJ', department: 'Development', designation: 'Full Stack Developer', email: 'mike.johnson@company.com' },
            { id: 3, name: 'Emily Chen', avatar: 'https://randomuser.me/api/portraits/women/68.jpg', initials: 'EC', department: 'Design', designation: 'UI Designer', email: 'emily.chen@company.com' },
            { id: 4, name: 'David Brown', avatar: 'https://randomuser.me/api/portraits/men/52.jpg', initials: 'DB', department: 'Development', designation: 'Backend Developer', email: 'david.brown@company.com' },
            { id: 5, name: 'Lisa Anderson', avatar: 'https://randomuser.me/api/portraits/women/82.jpg', initials: 'LA', department: 'QA', designation: 'QA Engineer', email: 'lisa.anderson@company.com' },
            { id: 6, name: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD', department: 'Management', designation: 'Project Manager', email: 'john.doe@company.com' },
            { id: 7, name: 'Jane Smith', avatar: 'https://randomuser.me/api/portraits/women/44.jpg', initials: 'JS', department: 'Design', designation: 'Lead Designer', email: 'jane.smith@company.com' }
        ],
        department: 'Design',
        taskList: 'UI/UX Tasks',
        startDate: 'Nov 28, 2024',
        endDate: 'Dec 5, 2024',
        estimatedTime: '16h',
        timeSpent: '12h 30m',
        timeLogged: '12.5h',
        progress: 75,
        description: 'Create a modern, responsive homepage design mockup for the e-commerce platform. The design should include hero section, featured products, categories, testimonials, and footer. Ensure mobile-first approach and follow brand guidelines.',
        project: 'eCommerce Website',
        projectId: '12345'
    };

    const activities = [
        { 
            id: 1, 
            type: 'status', 
            user: 'Sarah Wilson', 
            avatar: 'https://randomuser.me/api/portraits/women/50.jpg', 
            action: 'Changed status from', 
            details: 'Planning to In Progress', 
            time: '2 hours ago',
            timestamp: 'Dec 2, 2024 at 2:30 PM'
        },
        { 
            id: 2, 
            type: 'comment', 
            user: 'Mike Johnson', 
            avatar: 'https://randomuser.me/api/portraits/men/45.jpg', 
            action: 'Added a comment', 
            details: 'I have completed the hero section mockup. Please review.', 
            time: '5 hours ago',
            timestamp: 'Dec 2, 2024 at 11:15 AM'
        },
        { 
            id: 3, 
            type: 'file', 
            user: 'Sarah Wilson', 
            avatar: 'https://randomuser.me/api/portraits/women/50.jpg', 
            action: 'Uploaded attachment', 
            details: 'homepage-mockup-v1.fig', 
            time: '1 day ago',
            timestamp: 'Dec 1, 2024 at 3:45 PM'
        },
        { 
            id: 4, 
            type: 'priority', 
            user: 'John Doe', 
            avatar: 'https://randomuser.me/api/portraits/men/32.jpg', 
            action: 'Changed priority from', 
            details: 'Medium to High', 
            time: '2 days ago',
            timestamp: 'Nov 30, 2024 at 10:20 AM'
        },
        { 
            id: 5, 
            type: 'assignment', 
            user: 'John Doe', 
            avatar: 'https://randomuser.me/api/portraits/men/32.jpg', 
            action: 'Assigned task to', 
            details: 'Sarah Wilson, Mike Johnson', 
            time: '3 days ago',
            timestamp: 'Nov 29, 2024 at 9:00 AM'
        }
    ];

    const attachments = [
        { id: 1, name: 'homepage-mockup-v1.fig', size: '2.5 MB', uploadedBy: 'Sarah Wilson', uploadedDate: 'Dec 1, 2024', type: 'figma' },
        { id: 2, name: 'design-guidelines.pdf', size: '1.2 MB', uploadedBy: 'John Doe', uploadedDate: 'Nov 29, 2024', type: 'pdf' },
        { id: 3, name: 'reference-images.zip', size: '4.8 MB', uploadedBy: 'Sarah Wilson', uploadedDate: 'Nov 28, 2024', type: 'zip' },
        { id: 4, name: 'reference-images.zip', size: '4.8 MB', uploadedBy: 'Sarah Wilson', uploadedDate: 'Nov 28, 2024', type: 'zip' },
        { id: 5, name: 'reference-images.zip', size: '4.8 MB', uploadedBy: 'Sarah Wilson', uploadedDate: 'Nov 28, 2024', type: 'zip' },
        { id: 6, name: 'reference-images.zip', size: '4.8 MB', uploadedBy: 'Sarah Wilson', uploadedDate: 'Nov 28, 2024', type: 'zip' }
    ];

    const comments = [
        { 
            id: 1, 
            user: 'Mike Johnson', 
            avatar: 'https://randomuser.me/api/portraits/men/45.jpg', 
            initials: 'MJ',
            message: 'I have completed the hero section mockup. Please review and let me know if any changes needed.', 
            time: '5 hours ago',
            timestamp: 'Dec 2, 2024 at 11:15 AM'
        },
        { 
            id: 2, 
            user: 'Sarah Wilson', 
            avatar: 'https://randomuser.me/api/portraits/women/50.jpg', 
            initials: 'SW',
            message: 'Great work! The hero section looks amazing. Let\'s proceed with the product grid next.', 
            time: '3 hours ago',
            timestamp: 'Dec 2, 2024 at 1:30 PM'
        },
        { 
            id: 3, 
            user: 'John Doe', 
            avatar: 'https://randomuser.me/api/portraits/men/32.jpg', 
            initials: 'JD',
            message: 'Please ensure the design is mobile-responsive. This is a high priority task.', 
            time: '1 hour ago',
            timestamp: 'Dec 2, 2024 at 3:45 PM'
        }
    ];

    const getStatusColor = (status) => {
        const statusColors = {
            'Backlog': 'secondary',
            'To Do': 'info',
            'In Progress': 'warning',
            'TL Review': 'primary',
            'Reopened': 'danger',
            'PM Review': 'primary',
            'Send to Development': 'info',
            'Assign to QA': 'info',
            'Bug Fixing': 'danger',
            'QA Bug Verification': 'warning',
            'Deploy to Beta or Live': 'success',
            'On Hold': 'secondary',
            'Complete': 'success'
        };
        return statusColors[status] || 'secondary';
    };

    const getPriorityColor = (priority) => {
        const priorityColors = {
            'High': 'danger',
            'Medium': 'warning',
            'Low': 'success'
        };
        return priorityColors[priority] || 'secondary';
    };

    const getFileIcon = (type) => {
        const icons = {
            'figma': '🎨',
            'pdf': '📄',
            'zip': '📦',
            'doc': '📝',
            'image': '🖼️'
        };
        return icons[type] || '📎';
    };

    const getActivityIcon = (type) => {
        const icons = {
            'status': '🔄',
            'comment': '💬',
            'file': '📎',
            'priority': '⚡',
            'assignment': '👤'
        };
        return icons[type] || '📌';
    };

    return (
        <div className="task-detail-page">
            {/* ============================================ */}
            {/* TASK OVERVIEW SECTION */}
            {/* ============================================ */}
            <div className='c-card-1 mb-4'>
                <div className='c-card-header-1'>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                            <div className="fw-bold">Task Overview</div>
                        </div>
                        <div className='d-flex align-items-center gap-2'>
                            <div className="d-flex gap-2 me-3">
                                {/* Status Quick Edit Dropdown */}
                                <Dropdown className="c-dropdown-quick-edit">
                                    <Dropdown.Toggle 
                                        as="span"
                                        className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-sm-plus modern-badge badge-${getStatusColor(taskData.status)} cursor-pointer`}
                                    >
                                        {taskData.status}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {statusOptions.map((status) => (
                                            <Dropdown.Item 
                                                key={status}
                                                active={status === currentStatus}
                                                onClick={() => setCurrentStatus(status)}
                                            >
                                                <span 
                                                    className="badge-indicator" 
                                                    style={{ backgroundColor: `var(--bs-${getStatusColor(status)})` }}
                                                ></span>
                                                {status}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>

                                {/* Priority Quick Edit Dropdown */}
                                <Dropdown className="c-dropdown-quick-edit">
                                    <Dropdown.Toggle 
                                        as="span"
                                        className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-sm-plus modern-badge badge-${getPriorityColor(taskData.priority)} cursor-pointer`}
                                    >
                                        {taskData.priority} Priority
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {priorityOptions.map((priority) => (
                                            <Dropdown.Item 
                                                key={priority}
                                                active={priority === currentPriority}
                                                onClick={() => setCurrentPriority(priority)}
                                            >
                                                <span 
                                                    className="badge-indicator" 
                                                    style={{ backgroundColor: `var(--bs-${getPriorityColor(priority)})` }}
                                                ></span>
                                                {priority} Priority
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                            <Button 
                                variant='outline-primary' 
                                className='btn-sm text-nowrap'
                                onClick={() => setShowEditTask(true)}
                            >
                                Edit
                            </Button>
                            <Button 
                                variant='primary'
                                className='btn-sm text-nowrap'
                                onClick={() => setShowViewHistory(true)}
                            >
                                History
                            </Button>
                        </div>
                    </div>
                </div>
                <div className='c-card-body-1'>
                    <Row className="g-4">
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Task Name
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {taskData.name}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Start Date
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {taskData.startDate}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                End Date
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {taskData.endDate}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Created Date
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {taskData.createdDate}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Due Date
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {taskData.dueDate}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Project
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                <Link to={`/projects/detail`} className="text-decoration-none text-primary">
                                    {taskData.project}
                                </Link>
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Task ID
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {taskData.id}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Estimated Time
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {taskData.estimatedTime}
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* ============================================ */}
            {/* TASK STATISTICS CARDS */}
            {/* ============================================ */}
            <Row className="g-4 mb-4">
                {/* 1. Task Progress */}
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center justify-content-center bg-info bg-opacity-10 text-info rounded-3 icon-box-48">
                                <i className="fas fa-chart-line"></i>
                            </div>
                            <span className="fw-semibold small text-info">Status</span>
                        </div>
                        <div className="text-info fw-bold mb-1 fs-28">
                            {taskData.progress}%
                        </div>
                        <div className="text-secondary small mb-2">Task Progress</div>
                        <ProgressBar 
                            now={taskData.progress}
                            className="rounded-pill progress-sm"
                            variant="info"
                        />
                    </div>
                </Col>

                {/* 2. Time Logged */}
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary rounded-3 icon-box-48">
                                <i className="fas fa-clock"></i>
                            </div>
                            <span className="fw-semibold small text-success">On Track</span>
                        </div>
                        <div className="text-primary fw-bold mb-1 fs-28">{taskData.timeLogged}</div>
                        <div className="text-secondary small mb-2">Total Logged Hours</div>
                        <div className="d-flex justify-content-between small">
                            <span className="text-muted">Est: {taskData.estimatedTime}</span>
                            <span className="text-success fw-semibold">3.5h left</span>
                        </div>
                    </div>
                </Col>

                {/* 3. Assigned To */}
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success rounded-3 icon-box-48">
                                <i className="fas fa-users"></i>
                            </div>
                            <span className="fw-semibold small text-secondary">{taskData.assignedTo.length} Members</span>
                        </div>
                        <div className="text-success fw-bold mb-1 fs-28">{taskData.assignedTo.length}</div>
                        <div className="text-secondary small mb-2">Assigned Team Members</div>
                        <div className="d-flex align-items-center gap-1">
                            {taskData.assignedTo.slice(0, 5).map((member, index) => (
                                <div key={member.id} className={index > 0 ? 'avatar-overlap' : ''} style={{ zIndex: 5 - index }}>
                                    {member.avatar ? (
                                        <img 
                                            src={member.avatar} 
                                            alt={member.name} 
                                            className="avatar-32 rounded-circle border border-2 border-white" 
                                            title={member.name}
                                        />
                                    ) : (
                                        <div 
                                            className="avatar-32 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center border border-2 border-white fs-11" 
                                            title={member.name}
                                        >
                                            {member.initials}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {taskData.assignedTo.length > 5 && (
                                <div 
                                    className="avatar-32 avatar-overlap bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center border border-2 border-white cursor-pointer fs-11" 
                                    style={{ zIndex: 0 }}
                                    onClick={() => setShowViewAssignees(true)}
                                    role="button"
                                >
                                    +{taskData.assignedTo.length - 5}
                                </div>
                            )}
                        </div>
                    </div>
                </Col>

                {/* 4. Department / Task List */}
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center justify-content-center bg-warning bg-opacity-10 text-warning rounded-3 icon-box-48">
                                <i className="fas fa-folder"></i>
                            </div>
                            <span className="fw-semibold small text-warning">Category</span>
                        </div>
                        <div className="text-warning fw-bold mb-1 fs-28 text-truncate" title={taskData.department}>
                            {taskData.department}
                        </div>
                        <div className="text-secondary small mb-2">Department</div>
                        <div className="small">
                            <span className="text-muted">Task List: </span>
                            <span className="fw-semibold text-dark">{taskData.taskList}</span>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="g-3">
                {/* Right Column - Attachments */}
                <Col lg={8}>
                    {/* Attachments */}
                    <div className="c-card-1 h-100">
                        <div className="c-card-header-1">
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <div>Attachments</div>
                                <Button variant="outline-primary" size="sm" onClick={() => setShowUploadAttachment(true)}>
                                    <i className="bi bi-paperclip me-1"></i> Upload
                                </Button>
                            </div>
                        </div>
                        <div className="c-card-body-1">
                            <div className="border rounded-3 mb-3 p-3 bg-light">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <div className="text-secondary small mb-2">Total Files Uploaded</div>
                                        <div className="fw-bold text-primary fs-32">{attachments.length}</div>
                                    </div>
                                    <div 
                                        className="icon-box-60 d-flex align-items-center justify-content-center rounded-3 bg-info-subtle"
                                    >
                                        <i className="fas fa-folder-open fa-2x text-primary"></i>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                <div className="text-muted fw-semibold mb-1 text-uppercase text-sm-plus letter-spacing-wide">All Files</div>
                                <div className="attachments-scroll-container custom-scrollbar pe-2">
                                    <Row className="g-2">
                                        {attachments.map((file) => (
                                            <Col key={file.id} md={6}>
                                                <div className="d-flex align-items-center gap-3 p-3 border rounded-3 file-item-hover h-100">
                                                    <div className="d-flex align-items-center justify-content-center rounded-2 text-primary flex-shrink-0 bg-info-subtle icon-box-36">
                                                        <i className="fas fa-file"></i>
                                                    </div>
                                                    <div className="flex-grow-1 overflow-hidden">
                                                        <a href="#" className="text-decoration-none text-dark">
                                                            <div className="fw-semibold text-truncate small">{file.name}</div>
                                                        </a>
                                                        <small className="text-muted">{file.uploadedDate} • {file.size}</small>
                                                    </div>
                                                    <div 
                                                        className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle flex-shrink-0 file-download-icon cursor-pointer icon-box-32"
                                                        title="Download file"
                                                    >
                                                        <i className="fas fa-arrow-down text-primary small"></i>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
                {/* Left Column - Description, Subtasks, Attachments, Comments */}
                <Col lg={4}>
                    {/* Description */}
                    <div className="c-card-1 mb-3 h-100">
                        <div className="c-card-header-1">
                            <div>Description</div>
                        </div>
                        <div className="c-card-body-1">
                            <p className="p-tag mb-0">{taskData.description}</p>
                        </div>
                    </div>


                </Col>
                <Col sm={12}>
                    {/* Subtasks */}
                    <div className="c-card-1">
                        <div className="c-card-header-1">
                            <div>Subtasks</div>
                            <Button variant="outline-primary" size="sm" onClick={() => setShowAddSubtask(true)}>
                                <i className="bi bi-plus-lg me-1"></i> Add Subtask
                            </Button>
                        </div>
                        <div className="c-card-body-1 p-0">
                            <Table responsive className='c-table mb-0'>
                                <thead>
                                    <tr>
                                        <th width="50px" className='text-center'>
                                            <Form.Check />
                                        </th>
                                        <th>Task</th>
                                        <th width="100px" className='text-center'>Assignee</th>
                                        <th width="120px" className='text-center'>Start Date</th>
                                        <th width="120px" className='text-center'>Due Date</th>
                                        <th width="140px" className='text-center'>Workflow Status</th>
                                        <th width="100px" className='text-center'>Priority</th>
                                        <th width="100px" className='text-center'>Estimated</th>
                                        <th width="100px" className='text-center'>Logged</th>
                                        <th width="50px" className='text-center'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedSubtasks.map((subtask) => (
                                        <tr key={subtask.id}>
                                            <td className='text-center'>
                                                <Form.Check 
                                                    checked={subtask.completed}
                                                    onChange={() => toggleSubtaskCompletion(subtask.id)}
                                                />
                                            </td>
                                            <td>
                                                <div className='p-tag fw-bold'>{subtask.name}</div>
                                                <div className='p-tag'>{subtask.team}</div>
                                            </td>
                                            <td className='text-center'>
                                                <div className='d-flex align-items-center justify-content-center'>
                                                    {subtask.avatar ? (
                                                        <img 
                                                            src={subtask.avatar} 
                                                            alt={subtask.assignee} 
                                                            className="avatar-32 rounded-circle" 
                                                            title={subtask.assignee}
                                                        />
                                                    ) : (
                                                        <div 
                                                            className="c-avatar-text c-avatar-sm" 
                                                            title={subtask.assignee}
                                                        >
                                                            {subtask.initials}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className='text-center'>
                                                <div className='p-tag'>{subtask.startDate}</div>
                                            </td>
                                            <td className='text-center'>
                                                <div className='p-tag'>{subtask.dueDate}</div>
                                            </td>
                                            <td className='text-center'>
                                                <span className={`c-badge c-badge-${getStatusColor(subtask.status)}`}>
                                                    {subtask.status}
                                                </span>
                                            </td>
                                            <td className='text-center'>
                                                <span className={`c-badge c-badge-${getPriorityColor(subtask.priority)}`}>
                                                    {subtask.priority}
                                                </span>
                                            </td>
                                            <td className='text-center'>
                                                <div className='p-tag'>{subtask.estimated}</div>
                                            </td>
                                            <td className='text-center'>
                                                <div className='p-tag'>{subtask.logged}</div>
                                            </td>
                                            <td className='text-center'>
                                                <Dropdown className='c-dropdown-1'>
                                                    <Dropdown.Toggle variant="link">
                                                        <i className="fas fa-ellipsis-v"></i>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={() => {
                                                            setSelectedSubtask(subtask);
                                                            setShowEditSubtask(true);
                                                        }}>
                                                            <i className="fas fa-edit me-2"></i> Edit
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => {
                                                            setSelectedSubtask(subtask);
                                                            setShowMoveOrCopy(true);
                                                        }}>
                                                            <i className="fas fa-arrows-alt me-2"></i> Move or Copy
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => {
                                                            setSelectedSubtask(subtask);
                                                            setShowQuickAddSubtasks(true);
                                                        }}>
                                                            <i className="fas fa-plus-circle me-2"></i> Quickly Add Sub-task
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => {
                                                            // Handle duplicate logic
                                                            console.log('Duplicate subtask:', subtask);
                                                        }}>
                                                            <i className="fas fa-copy me-2"></i> Duplicate
                                                        </Dropdown.Item>
                                                        <Dropdown.Item className="text-danger" onClick={() => {
                                                            setSelectedSubtask(subtask);
                                                            setShowDeleteSubtask(true);
                                                        }}>
                                                            <i className="fas fa-trash me-2"></i> Delete
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        <div className='c-card-footer-1'>
                            <Pagination
                                pageSize={pageSize}
                                onPageSizeChange={setPageSize}
                                currentPage={currentPage}
                                onPageChange={setCurrentPage}
                                totalPages={totalPages}
                                totalItems={totalSubtasks}
                            />
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Modals */}
            {/* Edit Task Modal - Using listing page modal */}
            <AddTaskModal 
                show={showEditTask} 
                onHide={() => setShowEditTask(false)}
                taskData={taskData}
            />
            <AddSubtaskModal 
                show={showAddSubtask} 
                onHide={() => setShowAddSubtask(false)}
                taskId={taskId}
            />
            <UploadAttachmentModal 
                show={showUploadAttachment} 
                onHide={() => setShowUploadAttachment(false)}
                taskId={taskId}
            />
            <AddCommentModal 
                show={showAddComment} 
                onHide={() => setShowAddComment(false)}
                taskId={taskId}
            />
            <ViewHistoryOffcanvas 
                show={showViewHistory} 
                onHide={() => setShowViewHistory(false)}
                taskData={{
                    taskName: taskData.name,
                    projectName: taskData.project
                }}
            />
            <ChangeStatusModal 
                show={showChangeStatus} 
                onHide={() => setShowChangeStatus(false)}
                currentStatus={taskData.status}
                taskId={taskId}
            />
            <UpdatePriorityModal 
                show={showUpdatePriority} 
                onHide={() => setShowUpdatePriority(false)}
                currentPriority={taskData.priority}
                taskId={taskId}
            />

            {/* Filter Offcanvas */}
            <FilterTaskActivityOffcanvas 
                show={showActivityFilter} 
                onHide={() => setShowActivityFilter(false)}
                activityDateRange={activityDateRange}
                setActivityDateRange={setActivityDateRange}
                activityTypes={activityTypes}
                setActivityTypes={setActivityTypes}
                activityUsers={activityUsers}
                setActivityUsers={setActivityUsers}
            />

            {/* View Assignees Modal */}
            <ViewAssigneesModal 
                show={showViewAssignees} 
                onHide={() => setShowViewAssignees(false)}
                assignees={taskData.assignedTo}
            />

            {/* Subtask Action Modals - Using same modals as listing page */}
            <AddTaskModal
                show={showEditSubtask}
                onHide={handleEditSubtaskClose}
                taskData={selectedSubtask}
            />

            <MoveOrCopyTaskModal
                show={showMoveOrCopy}
                onHide={handleMoveOrCopyClose}
                moveOrCopyType={moveOrCopyType}
                setMoveOrCopyType={setMoveOrCopyType}
                targetProject={targetProject}
                setTargetProject={setTargetProject}
                targetTaskList={targetTaskList}
                setTargetTaskList={setTargetTaskList}
                projectOptions={projectOptions}
                taskListOptions={taskListOptions}
                onSubmit={handleMoveOrCopySubmit}
            />
            
            <QuickAddSubtasksModal
                show={showQuickAddSubtasks}
                onHide={handleQuickAddSubtasksClose}
                subtasksList={subtasksList}
                setSubtasksList={setSubtasksList}
                onSubmit={handleQuickAddSubtasksSubmit}
            />

            <DeleteTaskModal
                show={showDeleteSubtask}
                onHide={handleDeleteSubtaskClose}
                onConfirm={handleDeleteSubtask}
            />
        </div>
    );
};

export default TaskDetail;
